import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import dataStore from '../../../store/dataStore.ts'
import { ContactForm } from '../ContactForm.tsx'
import { IContact } from '../../../interfaces/types.ts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUserPlus } from '@fortawesome/free-solid-svg-icons/faUserPlus'
import { faPhone } from '@fortawesome/free-solid-svg-icons/faPhone'
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope'
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen'
import { PopupPortal } from '../../../components/common/Popup/PopupPortal.tsx'

export const ContactListDesktop: React.FC = () => {
    const contacts = dataStore(s => s.contacts).sort((a, b) => a.firstName > b.firstName ? 1 : -1)
    const pets = dataStore(s => s.pets)
    const navigate = useNavigate()
    const [editingContact, setEditingContact] = React.useState<IContact | null>(null)
    const [isNew, setIsNew] = React.useState(false)

    const handleEdit = (contact: IContact) => {
        setEditingContact(contact)
        setIsNew(false)
    }

    const handleAddNew = () => {
        setEditingContact(null)
        setIsNew(true)
    }

    const handleSaved = () => {
        setEditingContact(null)
        setIsNew(false)
    }

    const getPetNames = (petList: any[]) => {
        if (!petList?.length) return []
        return petList.map(petId => {
            const pet = pets.find(p => p._id === petId)
            return pet ? { name: pet.petName, image: pet.imageURL, color: pet.iconColor } : null
        }).filter(Boolean)
    }

    const emptyState = (
        <div className={'flex flex-col items-center gap-4 py-12 text-gray-400'}>
            <FontAwesomeIcon icon={faUserPlus} size={'3x'} />
            <div className={'text-lg'}>No alert contacts yet</div>
            <div className={'text-sm'}>Add someone to also receive alerts for your pets</div>
        </div>
    )

    const contactCards = contacts.map((contact: IContact) => {
        const assignedPets = getPetNames(contact.petList)

        return (
            <div key={contact._id}
                 className={'bg-white rounded-xl shadow-sm border border-base-300 p-4 flex flex-col gap-3 overflow-hidden'}>
                {/* Name + edit */}
                <div className={'flex items-center justify-between'}>
                    <div className={'text-xl font-bold text-primary'}>
                        {contact.firstName} {contact.lastName}
                    </div>
                    <button
                        className={'btn btn-ghost btn-sm btn-circle text-secondary'}
                        onClick={() => handleEdit(contact)}
                    >
                        <FontAwesomeIcon icon={faPen} />
                    </button>
                </div>

                {/* Contact info */}
                <div className={'flex flex-wrap gap-4 text-sm text-gray-600'}>
                    {contact.mobileNo && (
                        <div className={'flex items-center gap-1.5'}>
                            <FontAwesomeIcon icon={faPhone} className={'text-secondary/60'} size={'sm'} />
                            <span>{contact.mobileNo}</span>
                        </div>
                    )}
                    {contact.email && (
                        <div className={'flex items-center gap-1.5'}>
                            <FontAwesomeIcon icon={faEnvelope} className={'text-secondary/60'} size={'sm'} />
                            <span>{contact.email}</span>
                        </div>
                    )}
                </div>

                {/* Assigned pets */}
                {assignedPets.length > 0 && (
                    <div className={'flex flex-wrap items-center gap-2 pt-1'}>
                        <span className={'text-xs text-gray-400 uppercase tracking-wide'}>Alerts for:</span>
                        {assignedPets.map((pet: any) => (
                            <div key={pet.name} className={'flex items-center gap-1.5 bg-base-100 text-primary rounded-full px-3 py-1'}>
                                <img src={pet.image} className={'w-5 h-5 rounded-full object-cover shrink-0'} alt={pet.name} />
                                <span className={'text-xs font-semibold truncate max-w-[6rem]'}>{pet.name}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* SMS opt-in status */}
                <div className={'text-xs text-gray-400'}>
                    SMS alerts: {contact.smsOptIn === 'Opt-in' ? '✓ Opted in' : 'Opted out'}
                </div>
            </div>
        )
    })

    const showForm = editingContact || isNew

    return (
        <div className={'flex flex-col gap-6 w-full max-w-2xl mx-auto py-6 px-4'}>
            {/* Header */}
            <div className={'flex items-center justify-between'}>
                <div>
                    <h1 className={'text-2xl font-bold text-primary m-0'}>Alert Contacts</h1>
                    <p className={'text-sm text-gray-500 mt-1'}>People who also receive alerts for your pets</p>
                </div>
                <button className={'btn btn-secondary btn-sm gap-2'} onClick={handleAddNew}>
                    <FontAwesomeIcon icon={faUserPlus} />
                    Add Contact
                </button>
            </div>

            {/* Contact cards */}
            {contacts.length ? (
                <div className={'flex flex-col gap-3'}>
                    {contactCards}
                </div>
            ) : emptyState}

            {/* Edit/Add form in popup */}
            <PopupPortal open={!!showForm}>
                <div className={'p-2'}>
                    <ContactForm
                        key={isNew ? 'new' : editingContact?._id}
                        init={isNew ? undefined : (editingContact || undefined)}
                        isCreate={isNew}
                        onSaved={handleSaved}
                    />
                </div>
            </PopupPortal>

            {/* Close button */}
            <div className={'flex justify-center pt-4'}>
                <button className={'btn btn-secondary btn-ghost btn-sm text-primary'} onClick={() => navigate('/map')}>
                    Close
                </button>
            </div>
        </div>
    )
}
